// Submissions per day, as an area chart.
//
// Inline SVG rather than a charting library: this is one series against a time
// axis, and the smallest chart dependency on npm costs more transferred bytes
// than the whole dashboard route. The report already pays for React Flow, and
// it pays for it lazily on a tab nobody opens by accident.
//
// The axis is DENSE, not sparse: a day with no submissions is drawn as zero
// rather than skipped. A gap-free axis is what makes a quiet week look quiet;
// plotting only the days that have rows draws a flat line through them and
// reads as steady traffic.

import { useMemo } from 'react';
import type { Submission } from '@/lib/api';

/** One column of the chart. */
export interface DayCount {
  /** Midnight of the day, local time. */
  day: Date;
  count: number;
}

/** Bucket submissions into the last `days` days, most recent last.
 *
 *  Exported for its own test: the bucketing is the part with an off-by-one in
 *  it, and asserting it through a rendered SVG would test the renderer. */
export function submissionsByDay(subs: readonly Submission[], days: number): DayCount[] {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (days - 1));

  const buckets: DayCount[] = [];
  const index = new Map<number, number>();
  for (let i = 0; i < days; i += 1) {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    index.set(day.getTime(), i);
    buckets.push({ day, count: 0 });
  }

  for (const s of subs) {
    const at = new Date(s.submitted_at);
    if (Number.isNaN(at.getTime())) continue;
    at.setHours(0, 0, 0, 0);
    const slot = index.get(at.getTime());
    // Anything older than the window simply falls outside it. Clamping it into
    // the first column would pile the whole backlog onto one day.
    if (slot !== undefined) buckets[slot].count += 1;
  }
  return buckets;
}

const VIEW_W = 720;
const VIEW_H = 180;
const PAD_L = 28;
const PAD_B = 18;

export function SubmissionsChart({ data }: { data: DayCount[] }) {
  const { line, area, peak } = useMemo(() => {
    const peakCount = Math.max(1, ...data.map((d) => d.count));
    const stepX = data.length > 1 ? (VIEW_W - PAD_L) / (data.length - 1) : 0;
    const usableH = VIEW_H - PAD_B;
    const points = data.map((d, i) => {
      const x = PAD_L + i * stepX;
      const y = usableH - (d.count / peakCount) * (usableH - 8);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    return {
      peak: peakCount,
      line: points.join(' '),
      area: `${PAD_L},${usableH} ${points.join(' ')} ${VIEW_W},${usableH}`,
    };
  }, [data]);

  const total = data.reduce((n, d) => n + d.count, 0);
  if (total === 0) {
    return (
      <p className="text-muted text-sm">
        No submissions in this window. The axis covers the last {data.length} days.
      </p>
    );
  }

  return (
    <svg
      className="w-full"
      height={VIEW_H}
      role="img"
      aria-label={`Submissions per day over the last ${data.length} days, peaking at ${peak}`}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="none"
    >
      <title>{`Submissions per day, peak ${peak}`}</title>
      {/* Baseline and peak, so a reader can put a number on the shape. */}
      <line className="stroke-ink-800" x1={PAD_L} x2={VIEW_W} y1={VIEW_H - PAD_B} y2={VIEW_H - PAD_B} strokeWidth="1" />
      <text className="fill-ink-500" fontSize="11" x="0" y={VIEW_H - PAD_B}>0</text>
      <text className="fill-ink-500" fontSize="11" x="0" y="12">{peak}</text>

      <polygon className="fill-accent-500/[0.14]" points={area} />
      <polyline className="stroke-accent-500" fill="none" points={line} strokeWidth="2" />
    </svg>
  );
}
